import { Application } from 'pixi.js'
import { NetworkManager } from './network/NetworkManager'
import { TelegramSDK } from './utils/telegram'
import { MainMenuScreen } from './ui/screens/MainMenuScreen'
import { DeathScreen } from './ui/screens/DeathScreen'
import { EvacuationScreen } from './ui/screens/EvacuationScreen'
import { SessionOverScreen } from './ui/screens/SessionOverScreen'
import { SettingsScreen } from './ui/screens/SettingsScreen'
import { AdminScreen } from './ui/screens/AdminScreen'
import { TutorialScreen } from './ui/screens/TutorialScreen'
import { createTutorialSlides } from './ui/tutorial/slides'
import { GameV4 } from './game/GameV4'
import { NeonColors } from './ui/neon-theme'
import { showWarningToast } from './ui/WarningToast'
import { showErrorDialog } from './ui/ErrorDialog'
import { getGameContainer, installDesktopZoomGuard } from './ui/appShell'
import { loadRenderQuality } from './config/gameMode'
import { getBuildLines } from './buildInfo'
import {
  MIN_STAKE,
} from '@star-snake/shared'
import type { ClientPerfSnapshot } from '@star-snake/shared'
import type {
  DeathMessage,
  EvacuationMessage,
  SessionOverMessage,
  ViolationWarningMessage,
} from '@star-snake/shared'
import type { ClientErrorReportPayload } from './network/NetworkManager'

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string
        initDataUnsafe: Record<string, unknown>
        ready: () => void
        expand: () => void
        close: () => void
        setHeaderColor: (color: string) => void
        setBackgroundColor: (color: string) => void
        enableClosingConfirmation: () => void
        disableVerticalSwipes?: () => void
        enableVerticalSwipes?: () => void
        isVerticalSwipesEnabled?: boolean
        HapticFeedback?: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void
          selectionChanged: () => void
        }
      }
    }
    __STAR_SNAKE_APP_STATE__?: string
    __STAR_SNAKE_CLIENT_PERF__?: ClientPerfSnapshot
  }
}

// Global error handler to catch unhandled errors
window.addEventListener('error', (event) => {
  showError(`JS Error: ${event.message}`)
})
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason instanceof Error ? event.reason.message : String(event.reason)
  showError(`Async Error: ${msg}`)
})

let pixiApp: Application
interface ActiveGame {
  setOnExit(callback: () => void): void
  setOnAssignedSnake(callback: () => void): void
  setOnDeath(callback: (data: DeathMessage) => void): void
  setOnEvacuation(callback: (data: EvacuationMessage) => void): void
  setOnSessionOver(callback: (data: SessionOverMessage) => void): void
  setOnGameEnd(callback: () => void): void
  setOnViolationWarning(callback: (data: ViolationWarningMessage) => void): void
  requestRespawn(): void
  setDead(): void
  setAlive(): void
  pause(): void
  resume(): void
  triggerPortalEntry(): void
  triggerMapExplosion(): void
  getMyEntityId(): number
  getPerfUploadHealthSnapshot(): {
    attemptedCount: number
    succeededCount: number
    failedCount: number
    consecutiveFailureCount: number
    lastAttemptAtMs: number | null
    lastSuccessAtMs: number | null
    lastFailureAtMs: number | null
    lastHttpStatus: number | null
    lastError: string | null
  }
  dispose(): void
}

let currentGame: ActiveGame | null = null
let mainMenu: MainMenuScreen
let deathScreen: DeathScreen
let evacuationScreen: EvacuationScreen
let sessionOverScreen: SessionOverScreen
let settingsScreen: SettingsScreen
let adminScreen: AdminScreen
let tutorialScreen: TutorialScreen
type AppState =
  | 'menu'
  | 'joining'
  | 'playing'
  | 'death_overlay'
  | 'evacuation_overlay'
  | 'session_over_overlay'
  | 'disconnecting'
let appState: AppState = 'menu'
let sessionDisposers: Array<() => void> = []
let respawnRequested = false
let lastDeathData: DeathMessage | null = null
let currentRoomName: string | null = null

function publishAppState(next: AppState): void {
  window.__STAR_SNAKE_APP_STATE__ = next
  document.documentElement.dataset.starSnakeAppState = next
}

function getPerfSnapshotForErrorReport(): ClientErrorReportPayload['snapshot'] {
  const snapshot = window.__STAR_SNAKE_CLIENT_PERF__
  if (!snapshot) return null

  return {
    updatedAtMs: snapshot.updatedAtMs,
    fps: snapshot.fps,
    avgFrameDtMs: snapshot.avgFrameDtMs,
    maxFrameDtMs: snapshot.maxFrameDtMs,
    avgGameLoopMs: snapshot.avgGameLoopMs,
    inputAckMs: snapshot.inputAckMs,
    rttMs: snapshot.rttMs,
    pendingInputAckCount: snapshot.pendingInputAckCount,
    wsBufferedAmount: snapshot.wsBufferedAmount,
    pendingBinaryCount: snapshot.pendingBinaryCount,
    droppedPendingBinaryCount: snapshot.droppedPendingBinaryCount,
    gcPauseCount: snapshot.gcPauseCount,
    heapUsedMb: snapshot.heapUsedMb,
    longStallCount: snapshot.longStallCount,
    worstLongStallMs: snapshot.worstLongStallMs,
  }
}

function getUploadTelemetryForErrorReport(): ClientErrorReportPayload['telemetry'] {
  if (!currentGame) return null
  return currentGame.getPerfUploadHealthSnapshot()
}

function createClientErrorReport(
  screenMessage: string,
  debugMessage: string | null = null,
  roomNameOverride: string | null = null,
): ClientErrorReportPayload {
  const network = NetworkManager.getInstance()
  return {
    clientSessionId: network.clientDebugSessionId,
    occurredAtMs: Date.now(),
    screenMessage,
    debugMessage,
    roomName: roomNameOverride ?? currentRoomName,
    appState,
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
    buildLabel: getBuildLines().join(' | '),
    account: {
      telegramId: network.telegramId,
      displayName: network.defaultDisplayName || null,
      isAdmin: network.isAdmin,
      balance: network.balance,
      freeGamesLeft: network.freeGamesLeft,
    },
    snapshot: getPerfSnapshotForErrorReport(),
    telemetry: getUploadTelemetryForErrorReport(),
  }
}

function showError(
  message: string,
  options: {
    debugMessage?: string | null
    roomName?: string | null
  } = {},
): void {
  const debugMessage = options.debugMessage ?? null
  const network = NetworkManager.getInstance()
  const report = createClientErrorReport(message, debugMessage, options.roomName ?? null)

  showErrorDialog({
    title: 'Oops',
    message: 'Something went wrong. Please try again.',
    onSendLogs: async () => {
      await network.reportClientError(report)
    },
  })
}

function setPixiRenderingActive(active: boolean): void {
  if (!pixiApp) return
  if (active) {
    pixiApp.start()
    return
  }
  pixiApp.stop()
  // Render one static frame so canvas remains valid without continuous GPU work.
  pixiApp.render()
}

function clearSessionDisposers(): void {
  for (const dispose of sessionDisposers.splice(0)) {
    dispose()
  }
}

function hideBlockingOverlays(): void {
  deathScreen?.hide()
  evacuationScreen?.hide()
  sessionOverScreen?.hide()
}

function setAppState(next: AppState): void {
  appState = next
  publishAppState(next)
  const blocking = next === 'death_overlay' || next === 'evacuation_overlay' || next === 'session_over_overlay'
  if (blocking) {
    currentGame?.pause()
  }
}

async function showMainMenu(): Promise<void> {
  const network = NetworkManager.getInstance()
  setAppState('menu')
  hideBlockingOverlays()
  setPixiRenderingActive(false)
  await network.fetchBalance()
  mainMenu.show({
    onPlay: (stake: number, playerName: string) => {
      mainMenu.setJoining(true)
      startGame('game', stake, playerName)
    },
    onSettings: () => settingsScreen.show(() => {}),
    ...(network.isAdmin ? { onAdmin: () => showAdminPanel() } : {}),
    balance: network.balance,
    freeGamesLeft: network.freeGamesLeft,
    defaultName: network.defaultDisplayName,
  })
}

function showAdminPanel(): void {
  const network = NetworkManager.getInstance()
  adminScreen.show(network.getToken()!, () => showMainMenu())
}

async function startGame(roomName: string, stake: number, playerName: string): Promise<void> {
  const network = NetworkManager.getInstance()
  let assigned = false

  try {
    currentRoomName = roomName
    setAppState('joining')
    respawnRequested = false
    lastDeathData = null
    setPixiRenderingActive(true)
    const connection = await network.joinGame(roomName, stake, playerName)

    // Clean up previous game if any
    if (currentGame) {
      currentGame.dispose()
      currentGame = null
    }
    clearSessionDisposers()
    hideBlockingOverlays()

    currentGame = new GameV4(pixiApp, connection, {
      roomName,
      adminDebugEnabled: network.isAdmin,
    })
    const game = currentGame
    game.setOnAssignedSnake(() => {
      assigned = true
      void network.fetchBalance().then(() => {
        if (mainMenu.isVisible()) {
          mainMenu.refreshFromNetwork()
        }
      }).catch(() => { /* ignore */ })
      if (appState === 'joining' || respawnRequested) {
        hideBlockingOverlays()
        currentGame?.setAlive()
        respawnRequested = false
        if (mainMenu.isVisible()) {
          mainMenu.hide()
        }
        setAppState('playing')
      }
    })

    // Exit button in HUD
    game.setOnExit(() => {
      cleanupGame()
      showMainMenu()
    })
    game.setOnDeath((data: DeathMessage) => {
      if (!currentGame) return
      lastDeathData = data
      respawnRequested = false
      hideBlockingOverlays()
      currentGame.setDead()
      setAppState('death_overlay')
      void network.fetchBalance().catch(() => { /* ignore */ }).finally(() => {
        deathScreen.show(
          data,
          () => {
            cleanupGame()
            const playerName = localStorage.getItem('star-snake-player-name') || 'Player'
            startGame('game', MIN_STAKE, playerName)
          },
          () => {
            cleanupGame()
            showMainMenu()
          },
          {
            balance: network.balance,
            freeGamesLeft: network.freeGamesLeft,
            lastStake: MIN_STAKE,
          }
        )
      })
    })

    game.setOnEvacuation((data: EvacuationMessage) => {
      if (!currentGame) return
      const entityId = currentGame.getMyEntityId()
      hideBlockingOverlays()
      currentGame.triggerPortalEntry()
      currentGame.setDead()
      setAppState('evacuation_overlay')
      cleanupGame()
      void network.fetchBalance().catch(() => { /* ignore */ }).finally(() => {
        evacuationScreen.show(
        data,
        () => {
          // Play Again — start a new game with 1⭐ stake
          const playerName = localStorage.getItem('star-snake-player-name') || 'Player'
          startGame('game', MIN_STAKE, playerName)
        },
        () => {
          showMainMenu()
        },
          entityId,
          {
            balance: network.balance,
            freeGamesLeft: network.freeGamesLeft,
          },
        )
      })
    })

    game.setOnSessionOver((data: SessionOverMessage) => {
      if (!currentGame) return
      hideBlockingOverlays()
      setAppState('session_over_overlay')
      sessionOverScreen.show(data, () => {
        cleanupGame()
        showMainMenu()
      }, lastDeathData ? { deathReason: lastDeathData.reason, killerName: lastDeathData.killedByName } : undefined)
    })

    game.setOnGameEnd(() => {
      if (!currentGame) return
      if (appState === 'session_over_overlay') {
        return
      }
      hideBlockingOverlays()
      setAppState('menu')
      cleanupGame(true)
      showMainMenu()
    })

    game.setOnViolationWarning((data: ViolationWarningMessage) => {
      showWarningToast(data.count, data.max, data.reason)
    })

    // Listen for disconnect
    sessionDisposers.push(connection.onLeave((event) => {
      const failedDuringJoin = !assigned && appState === 'joining'
      const overlayActive = appState === 'death_overlay'
        || appState === 'evacuation_overlay'
        || appState === 'session_over_overlay'
      void network.uploadClientDisconnect({
        clientSessionId: network.clientDebugSessionId,
        roomName,
        disconnect: {
          occurredAtMs: Date.now(),
          code: event.code,
          reason: (event.reason || 'none').slice(0, 240),
          appState,
          documentHidden: document.hidden,
          failedDuringJoin,
          overlayActive,
          assignedDuringSession: assigned,
        },
        snapshot: window.__STAR_SNAKE_CLIENT_PERF__ ? {
          updatedAtMs: window.__STAR_SNAKE_CLIENT_PERF__.updatedAtMs,
          avgFrameDtMs: window.__STAR_SNAKE_CLIENT_PERF__.avgFrameDtMs,
          maxFrameDtMs: window.__STAR_SNAKE_CLIENT_PERF__.maxFrameDtMs,
          avgGameLoopMs: window.__STAR_SNAKE_CLIENT_PERF__.avgGameLoopMs,
          inputAckMs: window.__STAR_SNAKE_CLIENT_PERF__.inputAckMs,
          rttMs: window.__STAR_SNAKE_CLIENT_PERF__.rttMs,
          avgAckOverRttMs: window.__STAR_SNAKE_CLIENT_PERF__.avgAckOverRttMs,
          pendingInputAckCount: window.__STAR_SNAKE_CLIENT_PERF__.pendingInputAckCount,
          wsBufferedAmount: window.__STAR_SNAKE_CLIENT_PERF__.wsBufferedAmount,
          pendingBinaryCount: window.__STAR_SNAKE_CLIENT_PERF__.pendingBinaryCount,
          droppedPendingBinaryCount: window.__STAR_SNAKE_CLIENT_PERF__.droppedPendingBinaryCount,
          gcPauseCount: window.__STAR_SNAKE_CLIENT_PERF__.gcPauseCount,
          heapUsedMb: window.__STAR_SNAKE_CLIENT_PERF__.heapUsedMb,
        } : null,
        game: {
          phase: currentGame ? 'active' : 'none',
          playerEntityId: currentGame ? currentGame.getMyEntityId() : null,
          portalDistance: null,
          paused: appState !== 'playing' && appState !== 'joining',
          dead: appState === 'death_overlay' || appState === 'evacuation_overlay' || appState === 'session_over_overlay',
        },
        ui: {
          touchInputActive: false,
          touchHasAim: false,
          touchInputAgeMs: null,
          touchMoveAgeMs: null,
          touchStaleRecoveryCount: 0,
          benchmarkVisible: false,
          documentHidden: document.hidden,
          appState,
        },
        benchmark: {
          sessionId: null,
          active: false,
          phaseKey: null,
          phaseLabel: null,
          phaseIndex: null,
          phaseCount: null,
        },
        debugPilot: {
          active: false,
          mode: 'off',
        },
        telemetry: currentGame
          ? currentGame.getPerfUploadHealthSnapshot()
          : {
              attemptedCount: 0,
              succeededCount: 0,
              failedCount: 0,
              consecutiveFailureCount: 0,
              lastAttemptAtMs: null,
              lastSuccessAtMs: null,
              lastFailureAtMs: null,
              lastHttpStatus: null,
              lastError: null,
            },
      }).catch(() => { /* telemetry should never break gameplay */ })
      if (currentGame) {
        if (appState === 'disconnecting') return
        if (overlayActive) return
        cleanupGame(false)

        if (failedDuringJoin) {
          setAppState('menu')
          const msg = event.reason || 'Failed to start game'
          void network.fetchBalance().finally(() => {
            if (mainMenu.isVisible()) {
              mainMenu.refreshFromNetwork()
              mainMenu.setJoining(false)
            } else {
              void showMainMenu()
            }
            showError(msg, { roomName })
          })
          return
        }

        setAppState('disconnecting')
        showMainMenu()
      }
    }))
  } catch (err) {
    const debugMessage = err instanceof Error
      && 'debugMessage' in err
      && typeof err.debugMessage === 'string'
      ? err.debugMessage
      : null
    if (debugMessage) {
      console.warn('[joinGame]', debugMessage)
    }
    const msg = err instanceof Error ? err.message : 'Failed to join game'
    showError(msg, { debugMessage, roomName })
    await network.fetchBalance().catch(() => { /* ignore */ })
    if (mainMenu.isVisible()) {
      mainMenu.refreshFromNetwork()
      mainMenu.setJoining(false)
    } else {
      await showMainMenu()
    }
    setAppState('menu')
    setPixiRenderingActive(false)
  }
}

function cleanupGame(leaveServer: boolean = true): void {
  const network = NetworkManager.getInstance()
  respawnRequested = false
  currentRoomName = null
  if (leaveServer) {
    network.leaveGame().catch(() => { /* ignore */ })
  } else {
    network.clearConnection()
  }
  clearSessionDisposers()
  if (currentGame) {
    currentGame.dispose()
    currentGame = null
  }
  setPixiRenderingActive(false)
}

function updateLoadingProgress(percent: number): void {
  const bar = document.getElementById('loading-progress-bar')
  if (bar) {
    bar.style.width = `${percent}%`
  }
}

function showLoadingError(msg: string): void {
  const el = document.getElementById('loading-screen')
  if (!el) return

  el.innerHTML = `
    <div style="
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      width: 100%; height: 100%;
    ">
      <h1 style="
        font-size: 2.5rem; font-weight: 900; letter-spacing: 3px;
        color: ${NeonColors.danger};
        text-shadow: 0 0 20px ${NeonColors.dangerGlow}, 0 0 40px ${NeonColors.dangerGlow};
        margin-bottom: 1rem;
      ">ERROR</h1>
      <p style="
        color: ${NeonColors.textMuted}; max-width: 80%; text-align: center;
        word-break: break-word; margin-bottom: 2rem; font-size: 0.95rem;
      ">${msg}</p>
      <button id="loading-retry-btn" style="
        background: ${NeonColors.primary};
        border: none; border-radius: 12px; color: #fff;
        font-weight: bold; cursor: pointer;
        box-shadow: 0 0 20px ${NeonColors.primaryGlow}, 0 0 40px ${NeonColors.primaryGlow};
        text-shadow: 0 0 8px ${NeonColors.primaryGlow};
        padding: 14px 32px; font-size: 16px;
      ">RETRY</button>
    </div>
  `

  const retryBtn = el.querySelector('#loading-retry-btn')
  retryBtn?.addEventListener('click', () => {
    window.location.reload()
  })
}

async function main(): Promise<void> {
  // Initialize Telegram WebApp SDK
  TelegramSDK.init()
  installDesktopZoomGuard()
  publishAppState(appState)

  // Inject video splash screen into loading-screen
  const loadingEl = document.getElementById('loading-screen')
  const splashStartMs = Date.now()
  const SPLASH_MIN_MS = 3000

  if (loadingEl) {
    loadingEl.style.cssText = `
      position: absolute; inset: 0;
      background: #000; z-index: 9000; overflow: hidden;
    `
    loadingEl.innerHTML = `
      <style>
        @keyframes progressGlow {
          0%, 100% { box-shadow: 0 0 8px ${NeonColors.primaryGlow}; }
          50% { box-shadow: 0 0 16px ${NeonColors.primaryGlow}, 0 0 24px ${NeonColors.primaryGlow}; }
        }
      </style>
      <video id="splash-video" autoplay loop muted playsinline webkit-playsinline
        preload="auto"
        style="
          position: absolute; top: 50%; left: 50%;
          max-width: 100%; max-height: 100%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        " src="/splash.mp4"></video>
      <div style="
        position: absolute; bottom: 48px; left: 50%; transform: translateX(-50%);
        width: 220px; height: 6px; border-radius: 3px;
        background: rgba(255,255,255,0.15); overflow: hidden;
        backdrop-filter: blur(4px);
      ">
        <div id="loading-progress-bar" style="
          width: 0%; height: 100%; border-radius: 3px;
          background: ${NeonColors.primary};
          box-shadow: 0 0 8px ${NeonColors.primaryGlow};
          animation: progressGlow 1.5s ease-in-out infinite;
          transition: width 400ms ease-out;
        "></div>
      </div>
    `

    // Force video play on mobile (some browsers block autoplay)
    const videoEl = loadingEl.querySelector('#splash-video') as HTMLVideoElement | null
    if (videoEl) {
      videoEl.play().catch(() => { /* autoplay blocked — silent fallback to black bg */ })
    }
  }

  // Create UI screens
  mainMenu = new MainMenuScreen()
  deathScreen = new DeathScreen()
  evacuationScreen = new EvacuationScreen()
  sessionOverScreen = new SessionOverScreen()
  settingsScreen = new SettingsScreen()
  adminScreen = new AdminScreen()
  tutorialScreen = new TutorialScreen()

  // Create PixiJS application
  const renderQuality = loadRenderQuality()
  const dpr = window.devicePixelRatio || 1
  const resolutionMap = [1, Math.min(dpr, 1.5), Math.min(dpr, 2)]
  const antialiasMap = [false, false, true]
  const powerPrefMap: GPUPowerPreference[] = ['low-power', 'low-power', 'high-performance']

  pixiApp = new Application()
  await pixiApp.init({
    background: '#00020a',
    resizeTo: getGameContainer(),
    antialias: antialiasMap[renderQuality],
    resolution: resolutionMap[renderQuality],
    autoDensity: true,
    powerPreference: powerPrefMap[renderQuality],
  })
  pixiApp.ticker.maxFPS = 60
  pixiApp.ticker.minFPS = 20

  updateLoadingProgress(30)

  const container = document.getElementById('game-container')
  if (!container) {
    throw new Error('Game container not found')
  }
  container.appendChild(pixiApp.canvas)

  // Show tutorial slides immediately (auth runs in parallel)
  const { slides: tutorialSlides, durations: tutorialDurations } = createTutorialSlides()
  let authDone = false
  let authError: string | null = null

  tutorialScreen.show(tutorialSlides, tutorialDurations, () => {
    if (authError) {
      showLoadingError(authError)
      tutorialScreen.hide()
      return
    }
    tutorialScreen.hide()
    showMainMenu()
  })

  // Fade out splash video → tutorial is now visible underneath
  if (loadingEl) {
    loadingEl.style.transition = 'opacity 500ms ease-in-out'
    loadingEl.style.opacity = '0'
    await new Promise<void>((resolve) => setTimeout(resolve, 500))
    loadingEl.remove()
  }

  // Authenticate with server (parallel to tutorial viewing)
  updateLoadingProgress(60)
  const network = NetworkManager.getInstance()

  try {
    await network.authenticate()
    authDone = true
  } catch (err) {
    authError = err instanceof Error ? err.message : 'Authentication failed'
    if (!import.meta.env.PROD) {
      // In dev mode, allow tutorial viewing even without auth
      authDone = true
      authError = null
    } else {
      tutorialScreen.setLoadingProgress(100)
      return
    }
  }

  // Signal tutorial that loading is done
  tutorialScreen.setLoadingProgress(100)
}

main().catch((err) => {
  const msg = err instanceof Error ? err.message : 'Unknown error'
  showLoadingError(msg)
})
